import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { nanoid } from 'nanoid';
import { setRefernceAction, setExtraPolygonAction, extraPolygonSelector, referenceSelector } from 'domain/journal';
import { companyGeneralFeatureFlagsSelector } from 'domain/companies/companiesSelector';
import { ReferenceFactory } from 'domain/journal/helper';
import clipboardCopy from 'lib/clipboardCopy';

import { createPolygon } from 'pages/document/page/components/Polygons/helpers';

import Area from 'pages/document/page/components/Polygons/selectionArea';
import Polygon from 'pages/document/page/components/Polygons/polygon';
import ContextMenu, { defaultDataForContextMenu } from 'pages/document/page/components/Polygons/contextMenu';
import TranslatePopup from 'pages/document/page/components/Polygons/TranslatePopup';
import { PolygonsRoot, PolygonsArea, PolygonsCanvas } from 'pages/document/page/components/Polygons/StyledComponents';
import Popper from '@mui/material/Popper';
import withTheme from 'hoc/withTheme';

import cx from 'classnames';

const SIZE_PARAMS = ['width', 'height', 'scale', 'rotate'];

class Polygons extends React.Component {
  state = {
    selecting: false,
    extraPolygon: null,
    itemBeingDragged: null,
    isOpenContextMenu: false,
    isOpenTranslateTooltip: false,
    dataForContextMenu: defaultDataForContextMenu,
    textForTranslate: null,
  };

  shouldComponentUpdate(nextProps, nextState) {
    const {
      polygons,
      extraPolygon: { polygonId },
      gridReference,
    } = this.props;

    const {
      selecting,
      extraPolygon,
      itemBeingDragged,
      isOpenContextMenu,
      dataForContextMenu,
      isOpenTranslateTooltip,
      textForTranslate,
    } = this.state;
    const {
      polygons: nPolygons,
      extraPolygon: { polygonId: nPolygonId },
      gridReference: nGridReference,
    } = nextProps;
    const {
      selecting: nSelecting,
      extraPolygon: nExtraPolygon,
      itemBeingDragged: nItemBeingDragged,
      isOpenContextMenu: nIsOpenContextMenu,
      dataForContextMenu: nDataForContextMenu,
      isOpenTranslateTooltip: nIsOpenTranslateTooltip,
      textForTranslate: nTextForTranslate,
    } = nextState;

    if (itemBeingDragged !== nItemBeingDragged) return true;
    if (polygonId !== nPolygonId) return true;
    if (!gridReference.equals(nGridReference)) return true;
    if (extraPolygon !== nExtraPolygon || selecting !== nSelecting) return true;
    if (isOpenContextMenu !== nIsOpenContextMenu) return true;
    if (isOpenTranslateTooltip !== nIsOpenTranslateTooltip) return true;
    if (textForTranslate !== nTextForTranslate) return true;
    if (dataForContextMenu !== nDataForContextMenu) return true;

    const samePolygons = polygons.equals(nPolygons);
    return !(samePolygons && this.isSameSizeParams(nextProps));
  }

  componentDidUpdate(prevProps, prevState) {
    const { extraPolygon } = this.state;

    if (this.selectionArea && !this.isSameSizeParams(prevProps)) {
      this.selectionArea.init();
    }
    if (prevState.extraPolygon !== extraPolygon) {
      if (extraPolygon && extraPolygon.text) {
        this.handleTranslateTooltip(extraPolygon.text);
      } else {
        this.handleCloseTranslateTooltip();
      }
    }
  }

  onSelectionStart = () => {
    this.setState({
      selecting: true,
    });
  };

  onSelectionEnd = (idx, dmx) => {
    const styledPolygons = this.props.getStyledPolygons();
    const polygons = idx.map((i) => styledPolygons[i]);
    const extraPolygon = createPolygon(polygons, dmx);
    this.setState({
      selecting: false,
      extraPolygon,
    });
  };

  onSelectionRemove = () => {
    this.setState({
      extraPolygon: null,
    });
  };

  get polygons() {
    return this.props.polygons.data;
  }

  getDraggedPolygonId = () => {
    const { gridReference } = this.props;
    return gridReference.isEmpty() ? null : gridReference.first().polygonId;
  };

  documentRef = React.createRef();

  extraPolygonRef = React.createRef();

  isSameSizeParams(nextProps) {
    return SIZE_PARAMS.every((attr) => nextProps[attr] === this.props[attr]);
  }

  handleDragStart = (targetParams, text, id, type) => {
    const { setExtraPolygon, setReference, pageNumber } = this.props;
    const { left, top, width, height } = targetParams;

    this.referenceId = nanoid(10);
    this.setState({ itemBeingDragged: id });

    const params = ReferenceFactory({
      id: this.referenceId,
      x1: left + width,
      y1: top + height / 2 + window.scrollY,
      text,
      type,
      polygonId: id,
    });

    setExtraPolygon({
      id: this.referenceId,
      boundingPoly: { w: width, h: height, x: left, y: top, page_number: pageNumber },
      text,
      polygonId: id,
    });

    setReference({ params, id: this.referenceId });
  };

  createSelectionPlace = (el) => {
    const { getStyledPolygons, setArea, theme } = this.props;

    if (el) {
      this.selectionPlace = el;
      this.selectionArea = new Area(
        el,
        getStyledPolygons,
        this.onSelectionStart,
        this.onSelectionEnd,
        this.onSelectionRemove,
        theme,
      );
      setArea(this.selectionArea);
    }
  };

  handleContextMenu = ({ text, shapeRef }) => {
    this.setState({
      isOpenContextMenu: true,
      dataForContextMenu: {
        text,
        shapeRef,
      },
    });
  };

  handleTranslateTooltip = (text) => {
    const { features } = this.props;

    if (features.get('is_translate_enable')) {
      this.setState({
        isOpenTranslateTooltip: true,
        textForTranslate: text,
      });
    }
  };

  handleCloseContextMenu = () => {
    this.setState({ isOpenContextMenu: false, dataForContextMenu: defaultDataForContextMenu });
  };

  handleCloseTranslateTooltip = () => {
    this.setState({ isOpenTranslateTooltip: false, textForTranslate: null });
  };

  handleCopy = () => {
    const { dataForContextMenu } = this.state;
    const { text } = dataForContextMenu || '';

    clipboardCopy(text).then(() => console.info('Text copied!'));

    this.handleCloseContextMenu();

    // Clean the canvas area when context menu closes
    if (this.selectionArea) {
      this.selectionArea.areaClean();
    }
  };

  selectionArea;

  selectionPlace;

  referenceId;

  renderPolygon = (polygon) => {
    const { getStyledPolygon } = this.props;
    const draggedPolygonId = this.getDraggedPolygonId();
    return (
      <Polygon
        key={polygon.id}
        style={getStyledPolygon(polygon)}
        id={polygon.id}
        text={polygon.text}
        handleDrag={this.handleDragStart}
        handleContextMenu={this.handleContextMenu}
        sx={{ opacity: draggedPolygonId === polygon.id ? 1 : 0 }}
      />
    );
  };

  render() {
    const { width, height, scale } = this.props;
    const { extraPolygon, selecting, isOpenContextMenu, dataForContextMenu, textForTranslate, isOpenTranslateTooltip } =
      this.state;

    return (
      <PolygonsRoot ref={this.documentRef}>
        <PolygonsCanvas ref={this.createSelectionPlace} width={width * scale} height={height} />
        <PolygonsArea className={cx({ 'is-drawing': selecting })} id="Polygons-container">
          {this.polygons.map(this.renderPolygon)}
          {extraPolygon && (
            <div style={extraPolygon.style} ref={this.extraPolygonRef}>
              <Polygon
                key={extraPolygon.id}
                style={extraPolygon.style}
                id={extraPolygon.id}
                text={extraPolygon.text}
                handleDrag={this.handleDragStart}
                handleContextMenu={this.handleContextMenu}
                handleMouseDown={this.handleCloseTranslateTooltip}
                sx={{ opacity: 1, borderStyle: 'dashed' }}
              />
            </div>
          )}
        </PolygonsArea>
        {isOpenTranslateTooltip && (
          <Popper
            open={isOpenTranslateTooltip}
            anchorEl={this.extraPolygonRef?.current?.children[0]}
            modifiers={[
              {
                name: 'preventOverflow',
                options: {
                  boundary: this.documentRef.current || 'clippingParents', // limit within parent or default clipping parents
                },
              },
              {
                name: 'flip',
                enabled: true,
              },
            ]}
            placement="bottom-start"
          >
            <TranslatePopup text={textForTranslate} />
          </Popper>
        )}

        {isOpenContextMenu && (
          <Popper open={isOpenContextMenu} anchorEl={dataForContextMenu.shapeRef} placement="bottom-start">
            <ContextMenu onClose={this.handleCloseContextMenu} onClickCopy={this.handleCopy} />
          </Popper>
        )}
      </PolygonsRoot>
    );
  }
}

const mapPropsToDispatch = {
  setReference: setRefernceAction,
  setExtraPolygon: setExtraPolygonAction,
};

const mapStateToProps = (state) => ({
  extraPolygon: extraPolygonSelector(state),
  gridReference: referenceSelector(state),
  features: companyGeneralFeatureFlagsSelector(state),
});

export default compose(connect(mapStateToProps, mapPropsToDispatch), withTheme)(Polygons);
